import { Link } from 'react-router-dom';
import ImageGallery from '../../components/ImageGallery';
import ProductCTA from '../../components/ProductCTA';
import { TECHNICAL_SPECS, VALUE_PROPOSITIONS } from '../../constants/specifications';
import './RollFormingMachines.css';

// Import images
import mach1 from '../../assets/products/roll forming machines/mach1-enlarge.jpg';
import mach2 from '../../assets/products/roll forming machines/mach2-enlarge.jpg';
import mach3 from '../../assets/products/roll forming machines/mach3-enlarge.jpg';
import mach4 from '../../assets/products/roll forming machines/mach4-enlarge.jpg';
import mach5 from '../../assets/products/roll forming machines/mach5-enlarge.jpg';
import mach6 from '../../assets/products/roll forming machines/mach6-enlarge.jpg';
import picture01 from '../../assets/products/roll forming machines/picture01.jpg';
import picture02 from '../../assets/products/roll forming machines/picture02.jpg';
import picture03 from '../../assets/products/roll forming machines/picture03.jpg';
import picture04 from '../../assets/products/roll forming machines/picture04.jpg';
import picture05 from '../../assets/products/roll forming machines/picture05.jpg';

const RollFormingMachines = () => {
  const machineImages = [
    { src: mach1, alt: 'Roll Forming Machine 1', title: 'Complete Roll Forming Line' },
    { src: mach2, alt: 'Roll Forming Machine 2', title: 'Industrial Roll Former' },
    { src: mach3, alt: 'Roll Forming Machine 3', title: 'Custom Roll Forming Equipment' },
    { src: mach4, alt: 'Roll Forming Machine 4', title: 'High-Speed Roll Former' },
    { src: mach5, alt: 'Roll Forming Machine 5', title: 'Panel Roll Forming Machine' },
    { src: mach6, alt: 'Roll Forming Machine 6', title: 'Multi-Pass Roll Former' },
    { src: picture01, alt: 'Roll Forming Process 1', title: 'Roll Forming in Action' },
    { src: picture02, alt: 'Roll Forming Process 2', title: 'Precision Manufacturing' },
    { src: picture03, alt: 'Roll Forming Process 3', title: 'Quality Control Process' },
    { src: picture04, alt: 'Roll Forming Process 4', title: 'Final Product Inspection' },
    { src: picture05, alt: 'Roll Forming Process 5', title: 'Finished Roll Formed Parts' },
  ];

  const specifications = [
    { feature: 'Material Thickness', range: TECHNICAL_SPECS.materialThickness.range },
    { feature: 'Material Strength', range: TECHNICAL_SPECS.materialStrength.description },
    { feature: 'Roll Space', range: 'up to 65" standard' },
    { feature: 'Number of Passes', range: 'up to 40' },
    { feature: 'Shaft Diameter', range: 'up to 8" diameter' },
    { feature: 'Drive System', range: 'Variable speed AC/DC drives' },
    { feature: 'Control System', range: 'PLC with HMI interface' },
    { feature: 'Safety Features', range: 'Light curtains, emergency stops' },
  ];

  const machineTypes = [
    {
      title: 'Standard Roll Formers',
      description: 'Complete roll forming lines for standard profiles and shapes.',
      features: ['6-16 forming stations', 'Fixed tooling setup', 'Standard drive systems', 'Basic control panel']
    },
    {
      title: 'Custom Roll Formers',
      description: 'Engineered solutions for complex profiles and specialized applications.',
      features: ['Custom tooling design', 'Variable forming stations', 'Advanced control systems', 'Specialized drive options']
    },
    {
      title: 'Panel Machines',
      description: 'Specialized equipment for building panel and roofing applications.',
      features: ['Wide material handling', 'Seaming capabilities', 'Cut-to-length systems', 'Stacking options']
    },
    {
      title: 'High-Speed Lines',
      description: 'Production-focused systems for high-volume manufacturing.',
      features: ['Optimized for speed', 'Automated feeding', 'Quality monitoring', 'Minimal downtime design']
    }
  ];

  return (
    <div className="roll-forming-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Home</Link> / <Link to="/products">Products</Link> / Roll Forming Machines
        </nav>

        {/* Page Header */}
        <div className="page-header">
          <h1>Roll Forming Machines</h1>
          <p className="page-subtitle">
            Complete roll forming lines and equipment engineered for precision manufacturing
          </p>
        </div>

        {/* Overview Section */}
        <section className="overview-section">
          <div className="overview-content">
            <h2>Precision Roll Forming Solutions</h2>
            <p>
              Our roll forming machines are designed and built to deliver exceptional performance for 
              a wide range of industrial applications. From standard profiles to complex custom shapes, 
              our equipment provides the precision and reliability you need for consistent production.
            </p>
            <p>
              With {VALUE_PROPOSITIONS.experience.toLowerCase()}, we engineer complete 
              production lines that integrate seamlessly into your manufacturing process. Our machines 
              handle material thicknesses from {TECHNICAL_SPECS.materialThickness.range} with yield strengths up to {TECHNICAL_SPECS.materialStrength.max}.
            </p>
          </div>
        </section>

        {/* Machine Types Grid */}
        <section className="machine-types-section">
          <h2>Machine Types & Configurations</h2>
          <div className="machine-types-grid">
            {machineTypes.map((type, index) => (
              <div key={index} className="machine-type-card">
                <h3>{type.title}</h3>
                <p>{type.description}</p>
                <ul>
                  {type.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Specifications Table */}
        <section className="specifications-section">
          <h2>Technical Specifications</h2>
          <div className="specs-table-wrapper">
            <table className="specs-table">
              <thead>
                <tr>
                  <th>Specification</th>
                  <th>Range/Capability</th>
                </tr>
              </thead>
              <tbody>
                {specifications.map((spec, index) => (
                  <tr key={index}>
                    <td>{spec.feature}</td>
                    <td>{spec.range}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Image Gallery */}
        <section className="gallery-section">
          <h2>Equipment Gallery</h2>
          <p>Browse our roll forming machines and see the precision engineering in action.</p>
          <ImageGallery images={machineImages} columns={3} />
        </section>

        {/* CTA Section */}
        <ProductCTA 
          variant="engineering"
          primaryButtonText="Request Roll Former Quote"
          secondaryButtonText="Contact Engineering"
        />
      </div>
    </div>
  );
};

export default RollFormingMachines;